import { useDebouncedValue } from "@mantine/hooks";
import { useMutation, useQueries } from "@tanstack/react-query";
import { useRef } from "react";

import { quoteJob, startJob } from "../../../../api/generated/endpoints/generation/generation";
import type { Job, JobQuote, JobRequest } from "../../../../api/generated/model";
import { attemptKey, lostAttempt, type LostAttempt } from "../../../../platform/idempotency";
import { QUOTE_DEBOUNCE_MS } from "../../../../platform/jobs/priced-start";

/** One actor's job to start. */
export interface ActorStart<R extends JobRequest> {
  actorId: string;
  data: R;
}

/** The sum of several quotes: what the whole step costs. */
export function summed(quotes: readonly JobQuote[]): JobQuote {
  return {
    credits_min: quotes.reduce((sum, quote) => sum + quote.credits_min, 0),
    credits_max: quotes.reduce((sum, quote) => sum + quote.credits_max, 0),
    approximate: quotes.some((quote) => quote.approximate),
  };
}

/**
 * The Talking Head's two steps, a job per actor each (G8-talking-head): every actor's request
 * priced half a second after the form settles, the price their sum; the start sends each with
 * its own price and an Idempotency-Key of its own (a lost answer keeps its key, an actor at a
 * time). The jobs that started are told even when another actor's was refused, whose error is
 * the step's. `starts` is none while the step cannot run. An `unquoted` step (live's Advanced,
 * whose parent job is free and each of its steps priced as it starts) asks for no price and
 * sends no `expected_quote_max`.
 */
export function usePricedStarts<R extends JobRequest>(options: {
  teamId: string;
  brandId: string;
  starts: ActorStart<R>[] | undefined;
  onStarted: (jobs: { actorId: string; job: Job }[]) => void;
  unquoted?: boolean;
}) {
  const { teamId, brandId, starts, onStarted, unquoted = false } = options;
  const body =
    starts === undefined || unquoted ? undefined : JSON.stringify(starts.map((each) => each.data));
  const [settled] = useDebouncedValue(body, QUOTE_DEBOUNCE_MS);
  const pricing = settled === undefined ? [] : (JSON.parse(settled) as R[]);
  const quotes = useQueries({
    queries: pricing.map((data) => ({
      queryKey: ["quote", teamId, brandId, JSON.stringify(data)],
      queryFn: ({ signal }: { signal: AbortSignal }) => quoteJob(teamId, brandId, data, { signal }),
      retry: false,
      staleTime: 60_000,
    })),
  });
  const current = body !== undefined && settled === body;
  const priced =
    current && quotes.length > 0 && quotes.every((each) => each.data !== undefined)
      ? quotes.map((each) => each.data!)
      : undefined;
  const lost = useRef(new Map<string, LostAttempt>());
  const start = useMutation({
    mutationFn: async (sending: ActorStart<R>[]) => {
      const results = await Promise.allSettled(
        sending.map(async ({ actorId, data }) => {
          const key = attemptKey(data, lost.current.get(actorId));
          lost.current.delete(actorId);
          try {
            const job = await startJob(teamId, brandId, data, { "Idempotency-Key": key });
            return { actorId, job };
          } catch (error) {
            const attempt = lostAttempt(key, data, error);
            if (attempt !== undefined) lost.current.set(actorId, attempt);
            throw error;
          }
        }),
      );
      const started = results.flatMap((result) => (result.status === "fulfilled" ? [result.value] : []));
      if (started.length > 0) onStarted(started);
      const refused = results.find((result) => result.status === "rejected");
      if (refused !== undefined) throw refused.reason;
    },
  });
  return {
    priced: priced === undefined ? undefined : summed(priced),
    pricing: body !== undefined && (settled !== body || quotes.some((each) => each.isFetching)),
    quoteError: current ? (quotes.find((each) => each.isError)?.error ?? null) : null,
    starting: start.isPending,
    error: start.error,
    canStart: starts !== undefined && (unquoted || priced !== undefined) && !start.isPending,
    /** Sends each actor's job, with `shared` (fields every job of the step carries) on each. */
    start: (shared: Partial<R> = {}) => {
      if (starts === undefined) return;
      if (unquoted) {
        start.mutate(starts.map((each) => ({ ...each, data: { ...each.data, ...shared } })));
        return;
      }
      if (priced === undefined) return;
      start.mutate(
        starts.map((each, at) => ({
          actorId: each.actorId,
          data: { ...each.data, ...shared, expected_quote_max: priced[at]!.credits_max },
        })),
      );
    },
  };
}

/**
 * One talking head's start, simple or Advanced: a job per actor through `usePricedStarts`, every
 * one with the same `group`, so My Generations shows them as one row (a8's #416). A resend after
 * a lost answer keeps the group, as the start's key; a new start takes a new one. Live shows My
 * Generations once the videos start: one started is enough to go there.
 */
export function useGroupedStarts<R extends JobRequest & { group?: string | null }>(options: {
  teamId: string;
  brandId: string;
  starts: ActorStart<R>[] | undefined;
  onStarted: (job: Job) => void;
  unquoted?: boolean;
}) {
  const { onStarted, ...rest } = options;
  const group = useRef(crypto.randomUUID());
  const step = usePricedStarts({
    ...rest,
    onStarted: (jobs) => {
      group.current = crypto.randomUUID();
      onStarted(jobs[0]!.job);
    },
  });
  return { ...step, start: () => step.start({ group: group.current } as Partial<R>) };
}
